import { useVirtualizer } from '@tanstack/react-virtual'
import FbButton from 'components/FbUI/FbButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { cn } from 'components/UI/cn'
import Checkbox from 'components/Checkbox/checkbox'
import { useEffect, useMemo, useRef, useState } from 'react'
import { BsXCircleFill } from 'react-icons/bs'
import { PiMagnifyingGlass } from 'react-icons/pi'
import { AiOutlineLoading3Quarters } from 'react-icons/ai'
import { useDebounce } from 'use-debounce'
// Form import removed - using custom FilterSearchInput component
import { FilterButton } from '../components/FilterButton'
import { FilterCloseButton } from '../components/FilterCloseButton'
import { useRegisterFilterKey } from '../components/FilterModal'
import { FilterStore } from '../FilterStore'

export interface SelectFilterOption {
  value: string
  label: string
}

export interface SelectFilterPaginationProps {
  onLoadMore: () => void
  hasMore: boolean
  isLoadingMore: boolean
  onSearchChange: (search: string) => void
  totalCount?: number
}

export interface SelectFilterProps {
  filterKey: string
  title: string
  options?: SelectFilterOption[]
  filterStore: FilterStore
  hideSearch?: boolean
  pagination?: SelectFilterPaginationProps
}

export function SelectFilter(props: SelectFilterProps) {
  const {
    pendingValueFilters: pending,
    setPendingValueFilters: setPending,
    clearPendingValueFilter: clearPending,
    registerFilterTitle,
  } = props.filterStore()
  const filter = pending[props.filterKey] ?? []
  const isActive = !!filter.length
  const isPaginated = !!props.pagination

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [internalFilters, setInternalFilters] = useState(filter ?? [])
  // Snapshot of which values were selected when the popover opened. Drives
  // the "selected to the top" sort *only*; the live checkbox state still
  // tracks `internalFilters`. Without this, unchecking an item mid-edit
  // makes it jump down the list and visually "disappear" as the user
  // toggles. We refresh the snapshot on each open so the next session
  // reflects whatever was applied last.
  const [frozenSelectedValues, setFrozenSelectedValues] = useState<Set<string>>(
    () => new Set((filter ?? []).map((f) => f?.value))
  )

  // Debounced search for server-side filtering in paginated mode
  const [debouncedSearch] = useDebounce(search, 300)

  // Trigger server-side search when debounced search changes in paginated mode
  useEffect(() => {
    if (isPaginated && props.pagination?.onSearchChange) {
      props.pagination.onSearchChange(debouncedSearch)
    }
  }, [debouncedSearch, isPaginated])

  // For paginated mode, we don't filter client-side (server does it)
  // We still need to sort selected items to the top.
  //
  // Both the search-include rule and the selected-to-top sort use the
  // frozen snapshot rather than live `internalFilters` — that way toggling
  // a checkbox doesn't reflow the visual order mid-edit. The snapshot
  // refreshes on each popover open in `handleOpenChange` below.
  const filteredOptions = useMemo(() => {
    if (!props.options) return undefined

    let result: SelectFilterOption[]
    if (!isPaginated) {
      // Client-side filtering only for non-paginated mode
      // .filter() creates a new array, so sorting is safe
      result = props.options.filter((o) => {
        const regex = new RegExp(search, 'gi')
        const labelTest = regex.test(o.label)
        const wasSelected = frozenSelectedValues.has(o.value)
        return wasSelected || labelTest
      })
    } else {
      // In paginated mode, create a copy to avoid mutating props
      result = [...props.options]
    }

    // Sort selected items to the top, then alphabetically within each group.
    // Alphabetical sort is skipped in paginated mode: only the currently
    // loaded pages are present, so client-side sorting would reorder items as
    // more pages stream in. Paginated ordering is the server's responsibility.
    return result.sort((a, b) => {
      const isASelected = frozenSelectedValues.has(a.value) ? 1 : -1
      const isBSelected = frozenSelectedValues.has(b.value) ? 1 : -1
      if (isASelected !== isBSelected) return isBSelected - isASelected
      if (isPaginated) return 0
      return a.label.localeCompare(b.label)
    })
  }, [props.options, search, frozenSelectedValues, isPaginated])

  const activeCount = filter?.length ?? 0

  function handleInternalChange(option: SelectFilterOption, selected: boolean) {
    if (selected) {
      setInternalFilters((prev) =>
        prev.filter((f) => f?.value !== option.value)
      )
    } else {
      setInternalFilters((prev) => [...prev, option])
    }
  }

  function selectAll() {
    if (!props.options) return
    setInternalFilters(
      props.options.map((o) => ({
        value: o.value,
        label: o.label,
      }))
    )
  }

  function selectNone() {
    setInternalFilters([])
  }

  function clearFilters() {
    clearPending(props.filterKey)
  }

  function saveFilters() {
    setPending(
      props.filterKey,
      internalFilters.map((f) => ({
        value: f.value,
        label: f.label,
      }))
    )
  }

  // Syncs the internal filters with the filters in the context
  useEffect(() => {
    setInternalFilters(filter)
  }, [pending, open])

  useEffect(() => {
    registerFilterTitle(props.filterKey, props.title)
  }, [])

  // Register with the enclosing FilterGroup so it can auto-count active
  // filters in its collapsed-state bubble.
  useRegisterFilterKey(props.filterKey)

  // Cancel raises this so the next close cycle skips the auto-save. Closing
  // any other way (outside-click, Escape, Apply button) still commits.
  const skipNextSaveRef = useRef(false)

  function handleOpenChange(open: boolean) {
    if (open) {
      // Always rebuild internal state from the *applied* filter on open,
      // not from `internalFilters` itself. If the previous session was
      // cancelled, `internalFilters` still holds the abandoned edits —
      // pulling from `filter` (the committed store value) guarantees the
      // checkboxes and the selected-to-top snapshot both reflect what's
      // actually live in the table.
      const applied = filter ?? []
      setInternalFilters(applied)
      setFrozenSelectedValues(
        new Set(applied.map((f) => f?.value).filter(Boolean))
      )
    } else {
      if (skipNextSaveRef.current) {
        skipNextSaveRef.current = false
      } else {
        // Apply filters when the dropdown closes
        saveFilters()
      }
    }
    setOpen(open)
  }

  if (!props.options || !filteredOptions) return null

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <div className={'relative d-flex'}>
        <DropdownMenuTrigger asChild>
          <FilterButton
            open={open}
            title={props.title}
            isActive={isActive}
            count={activeCount}
          />
        </DropdownMenuTrigger>
        <FilterCloseButton
          isActive={isActive}
          open={open}
          onClick={() => clearFilters()}
        />
      </div>

      <DropdownMenuContent
        forceMount={true}
        side={'bottom'}
        className={cn(
          '!rounded-xl !border-neutral-200 !shadow-lg !bg-white w-[280px] lg:w-[260px] !p-3'
        )}
        sideOffset={6}
      >
        {/* Bulk-select header: tri-state checkbox toggles all (or none when
            already-all-selected) + title; Reset clears the selection. Hidden
            in paginated mode since we don't have the full set client-side. */}
        {!isPaginated && props.options && (
          <>
            <div className="flex items-center justify-between gap-2 pb-2">
              <div
                className="flex items-center gap-2 cursor-pointer min-w-0"
                onClick={() => {
                  if (internalFilters.length === props.options!.length) {
                    selectNone()
                  } else {
                    selectAll()
                  }
                }}
              >
                <Checkbox
                  checked={internalFilters.length === props.options!.length}
                  indeterminate={
                    internalFilters.length > 0 &&
                    internalFilters.length < props.options!.length
                  }
                  readOnly
                  wrapperClassName="flex-shrink-0 pointer-events-none"
                />
                <span className="text-sm font-semibold text-neutral-800 truncate">
                  Select all
                </span>
              </div>
              <button
                type="button"
                onClick={() => selectNone()}
                disabled={internalFilters.length === 0}
                className={cn(
                  'text-sm font-medium transition-colors',
                  internalFilters.length === 0
                    ? 'text-neutral-400 cursor-not-allowed'
                    : 'text-brand-active hover:text-brand-bright'
                )}
              >
                Reset
              </button>
            </div>
            <hr className="border-0 border-t border-neutral-200 -mx-3 mb-2" />
          </>
        )}

        {!props.hideSearch && (
          <FilterSearchInput
            value={search}
            onChange={setSearch}
            isFocused={searchFocused}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
        )}

        <ItemsList
          options={filteredOptions}
          onSelect={handleInternalChange}
          selected={internalFilters}
          column={props.filterKey}
          pagination={props.pagination}
        />

        <div className="mt-3 flex items-center justify-end gap-2">
          <FbButton
            size={'sm'}
            variant="secondary"
            type="button"
            onClick={() => {
              // Skip the auto-save in handleOpenChange so Cancel really
              // throws away in-popover edits instead of silently committing.
              skipNextSaveRef.current = true
              setOpen(false)
            }}
          >
            Cancel
          </FbButton>
          <FbButton
            size={'sm'}
            type="button"
            className={
              '!bg-brand-bright !text-white hover:!bg-brand-active active:!bg-brand-active disabled:!bg-neutral-100 disabled:!text-neutral-400 disabled:!border-neutral-100 disabled:hover:!bg-neutral-100'
            }
            data-testid={`select-filter-apply-${props.filterKey}`}
            // Stays enabled when a filter is currently applied (`isActive`)
            // so Reset doesn't trap the user — they can clear the
            // checkboxes and Apply the empty state to drop the filter.
            disabled={internalFilters.length === 0 && !isActive}
            onClick={() => {
              saveFilters()
              setOpen(false)
            }}
          >
            Apply
          </FbButton>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ItemsList(props: {
  options: SelectFilterOption[]
  selected: SelectFilterOption[]
  onSelect: (option: SelectFilterOption, selected: boolean) => void
  column: string
  pagination?: SelectFilterPaginationProps
}) {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtual = useVirtualizer({
    count: props.options?.length ?? 1,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
  })

  // Handle scroll to detect when user is near bottom
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!props.pagination) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 50

    if (
      isNearBottom &&
      props.pagination.hasMore &&
      !props.pagination.isLoadingMore
    ) {
      props.pagination.onLoadMore()
    }
  }

  return (
    <div
      className={'mt-2 w-full'}
      ref={parentRef}
      onScroll={handleScroll}
      style={{
        height: `150px`,
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <div
        style={{
          height: `${virtual.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtual.getVirtualItems().map((item) => {
          const o = props.options[item.index]
          const isSelected = props.selected.some((s) => s?.value === o?.value)
          if (!o) return null
          return (
            <div
              key={props.column + item.index}
              className={cn(
                'max-w-full cursor-pointer flex items-center py-2 px-2 gap-2 rounded-lg hover:bg-neutral-100 text-sm text-neutral-700 overflow-ellipsis whitespace-nowrap'
              )}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${item.size}px`,
                transform: `translateY(${item.start}px)`,
              }}
              // The Checkbox below has `pointer-events-none`, so a click on
              // the checkbox visual passes straight through to this handler
              // rather than firing twice (native click + label's synthetic
              // dispatch on the inner input) — the source of FIR-7036 where
              // clicks on the box appeared to do nothing.
              onClick={() => {
                props.onSelect(o, isSelected)
              }}
            >
              <Checkbox
                checked={isSelected}
                readOnly
                wrapperClassName="flex-shrink-0 pointer-events-none"
              />
              <div
                className={
                  'overflow-ellipsis overflow-hidden whitespace-nowrap'
                }
              >
                {o.label}
              </div>
            </div>
          )
        })}
      </div>
      {props.pagination?.isLoadingMore && (
        <div className="flex items-center justify-center py-2">
          <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin text-gray-400" />
        </div>
      )}
    </div>
  )
}

// Search input component
function FilterSearchInput({
  value,
  onChange,
  isFocused,
  onFocus,
  onBlur,
}: {
  value: string
  onChange: (value: string) => void
  isFocused: boolean
  onFocus: () => void
  onBlur: () => void
}) {
  const hasValue = value.length > 0

  const getContainerStyles = () => {
    if (isFocused) return searchContainerStyles.typing
    if (hasValue) return searchContainerStyles.filled
    return searchContainerStyles.default
  }

  const getIconColor = () => {
    if (isFocused) return 'text-brand' // #06512F
    if (hasValue) return 'text-fg-muted' // #C4C4C4
    return 'text-fg' // #191D23
  }

  return (
    <div className={cn('relative flex items-center', getContainerStyles())}>
      {/* Left icon - shown in default and filled states */}
      {!isFocused && (
        <PiMagnifyingGlass
          className={cn('w-5 h-5 flex-shrink-0', getIconColor())}
        />
      )}

      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder="Search"
        className={cn(
          'flex-1 bg-transparent border-none outline-none text-sm',
          'text-fg placeholder-fg-muted'
        )}
      />

      {/* Right icon - search icon when typing, clear button when filled */}
      {isFocused && (
        <PiMagnifyingGlass
          className={cn('w-5 h-5 flex-shrink-0', getIconColor())}
        />
      )}
      {!isFocused && hasValue && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="flex-shrink-0 p-0 mr-2 border-none bg-transparent cursor-pointer"
        >
          <BsXCircleFill className="w-[13px] h-[13px] text-fg-muted" />
        </button>
      )}
    </div>
  )
}

// Search container style definitions
const searchContainerStyles = {
  typing: cn(
    'bg-white',
    '!border',
    '!border-brand-active',
    'rounded-[6px]',
    'shadow-[0px_4px_8px_3px_rgba(6,81,47,0.1)]',
    'pl-2',
    'pr-3',
    'py-2',
    'gap-1'
  ),
  filled: cn(
    'bg-white',
    '!border',
    '!border-line',
    'rounded-[6px]',
    'pl-2',
    'pr-3',
    'py-2',
    'gap-1'
  ),
  default: cn(
    'bg-white',
    '!border',
    '!border-line',
    'rounded-[6px]',
    'pl-2',
    'pr-3',
    'py-2',
    'gap-1'
  ),
}

// Button style definitions
const filterButtonBase = cn(
  'flex-1',
  'h-8',
  'px-3',
  'text-sm',
  'font-medium',
  'rounded-md',
  'flex',
  'items-center',
  'justify-center',
  'transition-shadow'
)

const allButtonStyles = {
  selected: cn(
    '!bg-basil-50',
    '!border',
    '!border-basil-600',
    '!text-brand-active'
  ),
  deselected: cn(
    '!bg-white',
    '!border',
    '!border-neutral-300',
    '!text-neutral-700',
    'hover:!bg-neutral-50'
  ),
}

const noneButtonStyles = {
  selected: cn(
    '!bg-neutral-100',
    '!border',
    '!border-neutral-400',
    '!text-neutral-800'
  ),
  deselected: cn(
    '!bg-white',
    '!border',
    '!border-neutral-300',
    '!text-neutral-700',
    'hover:!bg-neutral-50'
  ),
}

export function FilterAllButton({
  isSelected,
  onClick,
}: {
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={cn(
        filterButtonBase,
        isSelected ? allButtonStyles.selected : allButtonStyles.deselected
      )}
      onClick={onClick}
    >
      All
    </button>
  )
}

export function FilterNoneButton({
  isSelected,
  onClick,
}: {
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={cn(
        filterButtonBase,
        isSelected ? noneButtonStyles.selected : noneButtonStyles.deselected
      )}
      onClick={onClick}
    >
      None
    </button>
  )
}
